import React from 'react';
import { format } from 'date-fns';

interface EmotionData {
  quadrant: string;
  emotion: string;
  activities: string[];
  companions: string[];
  timestamp: string;
}

interface EmotionEntryMessageProps {
  emotionData: EmotionData;
  textNote?: string;
}

const quadrantColors: Record<string, { bg: string; text: string }> = {
  'high-pleasant': { bg: 'bg-accent-500', text: 'text-accent-950' },
  'high-unpleasant': { bg: 'bg-secondary-500', text: 'text-white' },
  'low-pleasant': { bg: 'bg-primary-500', text: 'text-white' },
  'low-unpleasant': { bg: 'bg-base-600', text: 'text-base-100' },
  'unknown': { bg: 'bg-base-700', text: 'text-base-200' }
};

const quadrantLabels: Record<string, string> = {
  'high-pleasant': 'High Energy, Pleasant',
  'high-unpleasant': 'High Energy, Unpleasant',
  'low-pleasant': 'Low Energy, Pleasant',
  'low-unpleasant': 'Low Energy, Unpleasant',
  'unknown': 'Uncertain'
};

export const EmotionEntryMessage: React.FC<EmotionEntryMessageProps> = ({ 
  emotionData, 
  textNote 
}) => {
  const { quadrant, emotion, activities, companions, timestamp } = emotionData;
  const colorClasses = quadrantColors[quadrant] || quadrantColors.unknown;
  const label = quadrantLabels[quadrant] || 'Unknown';

  return (
    <div className="p-4 my-4 max-w-md bg-base-800 rounded-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs text-base-400">
          {format(new Date(timestamp), 'h:mm a')}
        </span>
        <div 
          className={`px-3 py-1 text-sm font-medium rounded-lg ${colorClasses.bg} ${colorClasses.text}`}
        >
          {emotion === 'unknown' ? "I don't know" : emotion}
        </div>
      </div>

      {/* Quadrant Label */}
      <div className="text-xs mb-2 text-base-500">{label}</div>

      {/* Context Tags */}
      <div className="flex flex-wrap gap-2 mb-2">
        {activities.map(activity => (
          <span 
            key={activity} 
            className="px-2 py-1 text-xs bg-base-700 text-base-100 rounded"
          >
            {activity}
          </span>
        ))}
        {companions.map(companion => (
          <span 
            key={companion} 
            className="px-2 py-1 text-xs bg-base-600 text-base-200 rounded"
          >
            {companion}
          </span>
        ))}
      </div>

      {/* Text Note */}
      {textNote && (
        <p className="mt-3 text-sm italic text-base-200">
          "{textNote}"
        </p>
      )}
    </div>
  );
};

export default EmotionEntryMessage;